// filepath: apps/web/src/design-system/Badge.tsx
//
// FR-UI-CORE-9 / FR-UI-OPS-3 — Badge primitive from the design system
// inventory (`docs/guides/design system/components/core/Badge.jsx`).
//
// Ported from the reference JSX to TypeScript for the production
// React SPA. All colors reference semantic design-system aliases via
// CSS custom properties; no raw hex values are introduced here.

import React from "react";

export type BadgeTone =
  "neutral" | "success" | "danger" | "warning" | "primary";

export interface BadgeProps {
  children?: React.ReactNode;
  /** Tone drives the semantic color pair. Default: `neutral`. */
  tone?: BadgeTone;
  /** Test hook forwarded onto the rendered element. */
  "data-testid"?: string;
  /** Extra className passthrough. */
  className?: string;
  /** Inline style passthrough (token-only). */
  style?: React.CSSProperties;
}

interface TonePalette {
  bg: string;
  text: string;
}

const TONE_PALETTES: Record<BadgeTone, TonePalette> = {
  neutral: { bg: "var(--gray-100)", text: "var(--gray-700)" },
  success: {
    bg: "var(--color-success-subtle)",
    text: "var(--color-success-subtle-text)",
  },
  danger: {
    bg: "var(--color-danger-subtle)",
    text: "var(--color-danger-subtle-text)",
  },
  warning: {
    bg: "var(--color-warning-subtle)",
    text: "var(--color-warning-text)",
  },
  primary: {
    bg: "var(--color-primary-subtle)",
    text: "var(--color-primary-subtle-text)",
  },
};

/**
 * Badge — small inline status pill. Pure presentation; no internal state.
 * Renders a `<span>` so it can sit inside both `<td>` cells and
 * inline-flex layout rows.
 */
export function Badge({
  children,
  tone = "neutral",
  style,
  className,
  ...rest
}: BadgeProps): React.ReactElement {
  const palette = TONE_PALETTES[tone] ?? TONE_PALETTES.neutral;
  return (
    <span
      {...rest}
      className={["ds-badge", `ds-badge--${tone}`, className]
        .filter(Boolean)
        .join(" ")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        fontSize: "var(--text-xs)",
        fontWeight: "var(--font-medium)",
        fontFamily: "var(--font-sans)",
        background: palette.bg,
        color: palette.text,
        borderRadius: "var(--radius-full)",
        ...style,
      }}
    >
      {children}
    </span>
  );
}
